// Clear old enquiries (Session ATZ, email system Phase 2, Item 4; Director decision DE-2 as recommended: bulk-mark as
// handled outside email, by category and age, with preview and audit).
//
// The office picks a filter (state, category, age in working days, aged only, sender domain), PREVIEWS the exact
// conversations it matches across the WHOLE queue (paged here, never a page of it), and marks them "handled outside email"
// (answered by phone, on site, or no longer relevant). Nothing is deleted: a conversation comes back by itself if the person
// writes again, and a whole clearance is undone from the list below. The commit quotes the preview's count and fingerprint,
// so the server refuses it if the queue changed in between, and the confirm names the count it will mark.
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import type { EnquiryBulkBatch, EnquiryBulkFilter, EnquiryBulkPreview, EnquiryTriageState } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { ConfirmButton } from "../../components/ConfirmButton";
import { Button } from "../../components/Button";
import { formatDate } from "../../lib/format";

const STATE_OPTIONS: { value: EnquiryTriageState; label: string }[] = [
  { value: "action_owed", label: "Needs a reply" },
  { value: "needs_chase", label: "Needs a chase" },
  { value: "awaiting_reply", label: "Awaiting their reply" },
];

const PAGE = 50;

/** The filter as the server takes it, from the form's own fields. Pure. */
export function backlogFilterFrom(form: { states: EnquiryTriageState[]; category: string; minAge: string; agedOnly: boolean; domain: string }): EnquiryBulkFilter {
  const filter: EnquiryBulkFilter = {};
  if (form.states.length > 0 && form.states.length < STATE_OPTIONS.length) filter.states = form.states;
  if (form.category) filter.categories = [form.category];
  const age = Number.parseInt(form.minAge, 10);
  if (Number.isFinite(age) && age >= 0) filter.minAgeWorkingDays = age;
  if (form.agedOnly) filter.agedOnly = true;
  const domain = form.domain.trim().toLowerCase().replace(/^@/, "");
  if (domain) filter.senderDomain = domain;
  return filter;
}

export function EnquiryBacklogPanel({ onChanged }: { onChanged?: () => void }) {
  const [states, setStates] = useState<EnquiryTriageState[]>(STATE_OPTIONS.map((s) => s.value));
  const [category, setCategory] = useState("");
  const [minAge, setMinAge] = useState("20");
  const [agedOnly, setAgedOnly] = useState(false);
  const [domain, setDomain] = useState("");
  const [note, setNote] = useState("");

  const [preview, setPreview] = useState<EnquiryBulkPreview | null>(null);
  const [previewFilter, setPreviewFilter] = useState<EnquiryBulkFilter | null>(null);
  const [categories, setCategories] = useState<{ key: string; label: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [marking, setMarking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [batches, setBatches] = useState<EnquiryBulkBatch[]>([]);
  const [undoing, setUndoing] = useState<string | null>(null);

  async function loadBatches() {
    try {
      setBatches((await api.enquiries.bulkHandled.batches()).batches);
    } catch {
      // The list of clearances is a convenience; a failure leaves it empty rather than blocking the panel.
    }
  }

  useEffect(() => {
    void loadBatches();
  }, []);

  const form = { states, category, minAge, agedOnly, domain };
  const filter = backlogFilterFrom(form);
  const emptyFilter = Object.keys(filter).length === 0;

  async function runPreview(offset = 0) {
    setLoading(true);
    setError(null);
    setDone(null);
    try {
      const res = await api.enquiries.bulkHandled.preview(filter, { limit: PAGE, offset });
      setCategories(res.categories);
      setPreview((prev) => (offset > 0 && prev && prev.fingerprint === res.fingerprint ? { ...res, rows: [...prev.rows, ...res.rows] } : res));
      setPreviewFilter(filter);
    } catch (err) {
      setError(friendlyApiError(err, "Could not preview that filter."));
    } finally {
      setLoading(false);
    }
  }

  // A change to the form makes the shown preview stale: it must be previewed again before anything is marked.
  const stale = preview != null && JSON.stringify(previewFilter) !== JSON.stringify(filter);

  async function mark() {
    if (!preview || stale) return;
    setMarking(true);
    setError(null);
    try {
      const res = await api.enquiries.bulkHandled.commit({ filter, expectedCount: preview.total, fingerprint: preview.fingerprint, ...(note.trim() ? { note: note.trim() } : {}) });
      setDone(res.note);
      setPreview(null);
      setNote("");
      await loadBatches();
      onChanged?.();
    } catch (err) {
      setError(friendlyApiError(err, "Nothing was marked."));
      // The queue moved: show the set as it now stands, for the person to confirm again.
      void runPreview(0);
    } finally {
      setMarking(false);
    }
  }

  async function undo(batchId: string) {
    setUndoing(batchId);
    setError(null);
    try {
      const res = await api.enquiries.bulkHandled.undo(batchId);
      setDone(`${res.restored} conversation${res.restored === 1 ? "" : "s"} put back on the queue.`);
      await loadBatches();
      onChanged?.();
    } catch (err) {
      setError(friendlyApiError(err, "Could not undo that clearance."));
    } finally {
      setUndoing(null);
    }
  }

  const input = "rounded-ele border border-ele-hairline bg-ele-surface px-2 py-1.5 text-sm text-ele-strong";

  return (
    <section className="space-y-4" aria-label="Clear old enquiries">
      <div className="rounded-ele border border-ele-hairline bg-ele-surface-soft p-4">
        <h2 className="font-heading text-lg text-ele-strong">Clear old enquiries</h2>
        <p className="mt-1 text-sm text-ele-body">
          Mark conversations that were dealt with outside email (by phone, on site, or no longer relevant) so they leave the queue. Nothing is
          deleted: a conversation comes back by itself if the person writes again, and a whole clearance can be undone below. A reply already
          approved to send is never touched.
        </p>

        <fieldset className="mt-4">
          <legend className="text-xs font-semibold text-ele-strong">Show conversations that</legend>
          <div className="mt-1 flex flex-wrap gap-3">
            {STATE_OPTIONS.map((s) => (
              <label key={s.value} className="flex items-center gap-1.5 text-sm text-ele-body">
                <input
                  type="checkbox"
                  checked={states.includes(s.value)}
                  onChange={(e) => setStates((cur) => (e.target.checked ? [...cur, s.value] : cur.filter((v) => v !== s.value)))}
                />
                {s.label}
              </label>
            ))}
          </div>
        </fieldset>

        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <label className="flex flex-col gap-1 text-xs font-semibold text-ele-strong">
            Kind of email
            <select value={category} onChange={(e) => setCategory(e.target.value)} className={input}>
              <option value="">Any kind</option>
              {categories.map((c) => (
                <option key={c.key} value={c.key}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs font-semibold text-ele-strong">
            At least this many working days old
            <input type="number" min={0} max={1000} value={minAge} onChange={(e) => setMinAge(e.target.value)} className={input} />
          </label>
          <label className="flex flex-col gap-1 text-xs font-semibold text-ele-strong">
            From this domain (optional)
            <input type="text" value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="example.co.uk" className={input} />
          </label>
          <label className="flex items-end gap-1.5 pb-1.5 text-sm text-ele-body">
            <input type="checkbox" checked={agedOnly} onChange={(e) => setAgedOnly(e.target.checked)} />
            Only those past the aged line
          </label>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Button type="button" onClick={() => void runPreview(0)} disabled={loading || emptyFilter || states.length === 0}>
            {loading ? "Checking..." : "Preview"}
          </Button>
          {emptyFilter && <span className="text-xs text-ele-body">Choose at least one part of the filter first.</span>}
        </div>
      </div>

      {error && (
        <p role="alert" className="rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
          {error}
        </p>
      )}
      {done && (
        <p role="status" className="rounded-ele border border-ele-green bg-ele-surface px-3 py-2 text-sm text-ele-strong">
          {done}
        </p>
      )}

      {preview && (
        <div className="rounded-ele border border-ele-hairline bg-ele-surface-soft p-4">
          <p className="text-sm text-ele-strong">
            <span className="font-semibold">{preview.total}</span> conversation{preview.total === 1 ? "" : "s"} match: {preview.filterDescription}.
          </p>
          {stale && <p className="mt-1 text-sm text-ele-body">The filter has changed since this preview. Preview again before marking.</p>}
          {preview.rows.length > 0 && (
            <table className="ele-list-table mt-3 w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-ele-body">
                  <th className="py-1 pr-2">Subject</th>
                  <th className="py-1 pr-2">From</th>
                  <th className="py-1 pr-2">Last message</th>
                  <th className="py-1 pr-2">Kind</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((r) => (
                  <tr key={r.id} className="border-t border-ele-hairline">
                    <td className="truncate py-1 pr-2 text-ele-strong">{r.subject ?? "(no subject)"}</td>
                    <td className="truncate py-1 pr-2 text-ele-body">{r.counterpartyName ?? r.counterpartyEmail ?? ""}</td>
                    <td className="py-1 pr-2 text-ele-body">
                      {formatDate(r.lastMessageAt)} ({r.ageWorkingDays} working day{r.ageWorkingDays === 1 ? "" : "s"})
                    </td>
                    <td className="truncate py-1 pr-2 text-ele-body">{r.categoryLabel}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {preview.rows.length < preview.total && (
            <button type="button" onClick={() => void runPreview(preview.rows.length)} disabled={loading} className="mt-2 font-menu text-xs tracking-wide text-ele-blue hover:text-ele-strong">
              Show more ({preview.total - preview.rows.length} not shown)
            </button>
          )}
          {preview.total > 0 && (
            <div className="mt-4 flex flex-wrap items-end gap-3">
              <label className="flex min-w-64 flex-1 flex-col gap-1 text-xs font-semibold text-ele-strong">
                Why (optional, kept on the record)
                <input type="text" value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} placeholder="Answered by phone" className={input} />
              </label>
              <ConfirmButton
                onConfirm={() => void mark()}
                label={`Mark ${preview.total} handled outside email`}
                confirmLabel={`Mark these ${preview.total}? Press again`}
                busy={marking}
                busyLabel="Marking..."
                disabled={stale}
                className="rounded-ele border border-ele-purple-accent px-3 py-1.5 font-menu text-xs tracking-wide text-ele-strong"
              />
            </div>
          )}
        </div>
      )}

      {batches.length > 0 && (
        <div className="rounded-ele border border-ele-hairline bg-ele-surface-soft p-4">
          <h3 className="font-heading text-base text-ele-strong">Earlier clearances</h3>
          <ul className="mt-2 space-y-2">
            {batches.map((b) => (
              <li key={b.batchId} className="flex flex-wrap items-center justify-between gap-3 text-sm text-ele-body">
                <span>
                  {formatDate(b.markedAt)}: {b.stillHandled} conversation{b.stillHandled === 1 ? "" : "s"} still marked handled outside email
                </span>
                <ConfirmButton
                  onConfirm={() => void undo(b.batchId)}
                  label="Undo"
                  confirmLabel="Put them back? Press again"
                  busy={undoing === b.batchId}
                  busyLabel="Undoing..."
                  className="font-menu text-xs tracking-wide text-ele-blue hover:text-ele-strong"
                />
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
